import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import { Email, CheckCircle, Error } from '@mui/icons-material';
import axios from '../utils/axios';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    // Get email from location state or URL params
    const emailFromState = location.state?.email;
    const emailFromParams = searchParams.get('email');
    const token = searchParams.get('token');
    
    if (emailFromState) {
      setEmail(emailFromState);
    } else if (emailFromParams) {
      setEmail(emailFromParams);
    }

    // If there's a token in the URL, automatically verify
    if (token) {
      handleVerification(token);
    } else if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location.state, searchParams]);

  const handleVerification = async (token) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/api/verify-email', { token });
      setSuccess(true);
      setMessage(response.data.message);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setResendLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/api/resend-verification', { email });
      setMessage(response.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={60} />
            <Typography variant="h6">Verifying your email...</Typography>
          </Box>
        ) : success ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
            <Typography variant="h5" color="success.main">
              Email Verified!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting to login page...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Email sx={{ fontSize: 60, color: 'primary.main' }} />
            <Typography variant="h5" gutterBottom>
              Verify Your Email
            </Typography>
            
            {message && (
              <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                {message}
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
            </Typography>
            
            <Alert severity="warning" sx={{ width: '100%', mb: 3 }}>
              <Typography variant="body2">
                <strong>Can't find the email?</strong> Please check your <strong>Junk/Spam folder</strong> as verification emails sometimes end up there. 
                You can also mark GymRat emails as "Not Spam" to ensure future emails arrive in your inbox.
              </Typography>
            </Alert>
            
            <Box sx={{ width: '100%', mb: 3 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                disabled={!!location.state?.email}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResendVerification}
                disabled={resendLoading || !email}
                startIcon={resendLoading ? <CircularProgress size={20} /> : <Email />}
              >
                {resendLoading ? 'Sending...' : 'Resend Verification Email'}
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/register')}
              >
                Register Again
              </Button>
            </Box>
            
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default VerifyEmail;
